import type {
	CreditorDto,
	DebtPaymentDto,
	RevampedDebtRecordDto,
} from '#modules/DebtOverview/types/debtOverviewApi.types'

import type { DebtItem, DebtItemsAndDebtPayments } from '../debtItem'

import { generateGuid } from '../generateGuid'

interface RawDebtData {
	debtRecords: RevampedDebtRecordDto[]
	debtPayments: DebtPaymentDto[]
	allDebtPayments: DebtPaymentDto[]
	creditors: CreditorDto[]
}

interface CreditorDescriptor {
	logo: string
	name: string
}

export const transformDebtData = (
	rawData: RawDebtData,
): DebtItemsAndDebtPayments => {
	const debtItems = mapDebtEstimatesToDebtItems(rawData)

	return {
		debtItems,
		debtPayments: rawData.allDebtPayments ?? [],
	}
}

const getCreditorMap = (
	creditors: CreditorDto[],
): Map<number, CreditorDescriptor> => {
	return new Map(
		creditors.map((creditor) => [
			creditor.id,
			{
				logo: creditor.logo ? `data:image/png;base64,${creditor.logo}` : '',
				name: creditor.name,
			},
		]),
	)
}

const mapDebtEstimatesToDebtItems = ({
	debtRecords,
	creditors,
	debtPayments,
}: Omit<RawDebtData, 'allDebtPayments'>): DebtItem[] => {
	const creditorMap = getCreditorMap(creditors)
	const debtItemMap = new Map<string, DebtItem>()

	debtRecords.forEach((record) => {
		const debtSetId = record.paymentId ?? record.id
		const existingDebtItem = debtItemMap.get(debtSetId)
		if (existingDebtItem != null) {
			existingDebtItem.debtRecords = [...existingDebtItem.debtRecords, record]
			debtItemMap.set(debtSetId, { ...existingDebtItem })
			return
		}

		const creditor =
			record.monthioCreditorId !== null && !isNaN(record.monthioCreditorId)
				? creditorMap.get(record.monthioCreditorId)
				: undefined
		debtItemMap.set(debtSetId, {
			id: record.id,
			creditor,
			context: 'CREATED_BY_MONTHIO',
			debtRecords: [record],
			debtPayment: {
				id: `DPR_ABU_${generateGuid()}`,
				currency: record.currency,
				paymentAmount: null,
				frequency: 'monthly',
				monthioCreditorId: record.monthioCreditorId,
				debtRecordId: record.id,
			},
		})
	})

	debtPayments.forEach((payment) => {
		const debtItem = debtItemMap.get(payment.id)
		if (debtItem) {
			debtItem.debtPayment = payment
		}
	})
	return Array.from(debtItemMap.values())
}
